import { useMemo } from 'react';
import { Link } from 'react-router';
import { EmptyState, Spinner, Stack, Text } from '../../ui';
import { defaultCentre } from '../../events/defaultCentre';
import { useEventSearch } from '../../events/queries';
import type { EventSearchParams } from '../../events/types';
import { EventCard } from '../events/EventCard';
import { landingCopy } from './copy';

/**
 * The centre for the landing preview. Unlike Discover, the landing never asks a signed-out visitor
 * for their location — a public page should not trigger a permission prompt — so it centres on the
 * launch metro, shows a wide window of upcoming listings, and links out to full discovery.
 */
const PREVIEW_CENTRE = defaultCentre();

/** How many events to preview before sending the visitor to full discovery. */
const PREVIEW_LIMIT = 3;

/**
 * A small preview of real upcoming public events, so a signed-out visitor sees genuine activity
 * rather than only marketing copy.
 *
 * The section degrades quietly: it shows a spinner while loading, an invitational empty state when
 * there is nothing upcoming, and hides itself entirely on error rather than breaking the page.
 *
 * @returns The upcoming-events section, or `null` when the load failed.
 */
export function UpcomingEventsPreview(): React.JSX.Element | null {
  const params: EventSearchParams = useMemo(
    () => ({
      lat: PREVIEW_CENTRE.lat,
      lon: PREVIEW_CENTRE.lon,
      radiusKm: 250,
      from: new Date().toISOString(),
    }),
    [],
  );

  const { data, isPending, isError } = useEventSearch(params);

  // Hide the section on error: an empty gap is better than a broken panel on a first impression.
  if (isError) {
    return null;
  }

  const events = (data ?? []).slice(0, PREVIEW_LIMIT);

  return (
    <section className="gm-landing__section" aria-labelledby="gm-landing-upcoming">
      <div className="gm-landing__section-head">
        <h2 id="gm-landing-upcoming" className="gm-landing__section-title">
          {landingCopy.upcomingTitle}
        </h2>
        <Link className="gm-button gm-button--secondary" to="/events">
          {landingCopy.browseAll}
        </Link>
      </div>

      {isPending && (
        <Stack gap="md" align="center">
          <Spinner label="Loading events" />
          <Text muted>Loading upcoming events…</Text>
        </Stack>
      )}

      {!isPending && events.length === 0 && (
        <EmptyState
          title="No upcoming events yet"
          description="Be the first — create an account and add a meet, show, or race."
        />
      )}

      {!isPending && events.length > 0 && (
        <div className="gm-landing__events gm-event-list">
          {events.map((event) => (
            <EventCard key={event.id} event={event} />
          ))}
        </div>
      )}
    </section>
  );
}
